import { StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';

const styles = StyleSheet.create({
  container: {
    fontSize: 8,
    fontFamily: 'Open Sans',
    lineHeight: 1.5,
  },
  paragraph: {
    marginBottom: 4,
  },
  text: {
    fontSize: 8,
    fontFamily: 'Open Sans',
  },
  strong: {
    fontWeight: 700,
  },
  italic: {
    fontStyle: 'italic',
  },
  underline: {
    textDecoration: 'underline',
  },
  strikethrough: {
    textDecoration: 'line-through',
  },
  listContainer: {
    marginBottom: 4,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  listBullet: {
    width: 15,
    fontSize: 8,
    fontFamily: 'Open Sans',
  },
  listContent: {
    flex: 1,
    fontSize: 8,
    fontFamily: 'Open Sans',
  },
});

interface HtmlPdfProps {
  html?: string;
}

// Simple token-based parser for HTML
interface Token {
  type: 'text' | 'tag_open' | 'tag_close';
  value: string;
  tagName?: string;
}

const tokenize = (html: string): Token[] => {
  const tokens: Token[] = [];
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
  let lastIndex = 0;
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    // Add text before this tag
    if (match.index > lastIndex) {
      const text = html.slice(lastIndex, match.index);
      if (text) {
        tokens.push({ type: 'text', value: decodeHtmlEntities(text) });
      }
    }

    const fullTag = match[0];
    const tagName = match[1].toLowerCase();
    const isClosing = fullTag.startsWith('</');

    tokens.push({
      type: isClosing ? 'tag_close' : 'tag_open',
      value: fullTag,
      tagName,
    });

    lastIndex = tagRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < html.length) {
    const text = html.slice(lastIndex);
    if (text) {
      tokens.push({ type: 'text', value: decodeHtmlEntities(text) });
    }
  }

  return tokens;
};

const decodeHtmlEntities = (text: string): string => {
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

interface ParsedNode {
  type: 'text' | 'element';
  value?: string;
  tagName?: string;
  children?: ParsedNode[];
}

const parseTokens = (tokens: Token[]): ParsedNode[] => {
  const result: ParsedNode[] = [];
  let i = 0;

  const parseChildren = (endTag?: string): ParsedNode[] => {
    const children: ParsedNode[] = [];

    while (i < tokens.length) {
      const token = tokens[i];

      if (token.type === 'tag_close') {
        if (token.tagName === endTag) {
          i++;
          return children;
        }
        // Mismatched close tag, skip it
        i++;
        continue;
      }

      if (token.type === 'text') {
        if (token.value.trim() || token.value.includes(' ')) {
          children.push({ type: 'text', value: token.value });
        }
        i++;
        continue;
      }

      if (token.type === 'tag_open') {
        const tagName = token.tagName!;
        i++;

        // Self-closing tags
        if (tagName === 'br') {
          children.push({ type: 'element', tagName, children: [] });
          continue;
        }

        const elementChildren = parseChildren(tagName);
        children.push({ type: 'element', tagName, children: elementChildren });
      }
    }

    return children;
  };

  while (i < tokens.length) {
    const nodes = parseChildren();
    result.push(...nodes);
  }

  return result;
};

const HtmlPdf: React.FC<HtmlPdfProps> = ({ html }) => {
  if (!html) return null;

  const tokens = tokenize(html);
  const parsed = parseTokens(tokens);

  const renderInlineContent = (
    nodes: ParsedNode[],
    keyPrefix = '',
  ): React.ReactNode => {
    return nodes.map((node, idx) => {
      const key = `${keyPrefix}-${idx}`;

      if (node.type === 'text') {
        return node.value;
      }

      if (node.type === 'element') {
        const children = node.children || [];

        switch (node.tagName) {
          case 'strong':
          case 'b':
            return (
              <Text key={key} style={styles.strong}>
                {renderInlineContent(children, key)}
              </Text>
            );
          case 'em':
          case 'i':
            return (
              <Text key={key} style={styles.italic}>
                {renderInlineContent(children, key)}
              </Text>
            );
          case 'u':
            return (
              <Text key={key} style={styles.underline}>
                {renderInlineContent(children, key)}
              </Text>
            );
          case 's':
          case 'strike':
          case 'del':
            return (
              <Text key={key} style={styles.strikethrough}>
                {renderInlineContent(children, key)}
              </Text>
            );
          case 'br':
            return '\n';
          case 'span':
            return <Text key={key}>{renderInlineContent(children, key)}</Text>;
          default:
            return renderInlineContent(children, key);
        }
      }

      return null;
    });
  };

  const renderNodes = (
    nodes: ParsedNode[],
    keyPrefix = '',
  ): React.ReactNode => {
    return nodes.map((node, idx) => {
      const key = `${keyPrefix}-${idx}`;

      if (node.type === 'text') {
        // Top-level text, wrap in Text
        if (node.value?.trim()) {
          return (
            <Text key={key} style={styles.text}>
              {node.value}
            </Text>
          );
        }
        return null;
      }

      if (node.type === 'element') {
        const children = node.children || [];

        switch (node.tagName) {
          case 'p':
            return (
              <View key={key} style={styles.paragraph}>
                <Text style={styles.text}>
                  {renderInlineContent(children, key)}
                </Text>
              </View>
            );
          case 'ul':
            return (
              <View key={key} style={styles.listContainer}>
                {children
                  .filter((child) => child.tagName === 'li')
                  .map((li, liIdx) => (
                    <View key={`${key}-li-${liIdx}`} style={styles.listItem}>
                      <Text style={styles.listBullet}>•</Text>
                      <Text style={styles.listContent}>
                        {renderInlineContent(
                          li.children || [],
                          `${key}-li-${liIdx}`,
                        )}
                      </Text>
                    </View>
                  ))}
              </View>
            );
          case 'ol':
            return (
              <View key={key} style={styles.listContainer}>
                {children
                  .filter((child) => child.tagName === 'li')
                  .map((li, liIdx) => (
                    <View key={`${key}-li-${liIdx}`} style={styles.listItem}>
                      <Text style={styles.listBullet}>{liIdx + 1}.</Text>
                      <Text style={styles.listContent}>
                        {renderInlineContent(
                          li.children || [],
                          `${key}-li-${liIdx}`,
                        )}
                      </Text>
                    </View>
                  ))}
              </View>
            );
          case 'strong':
          case 'b':
          case 'em':
          case 'i':
          case 'u':
          case 's':
          case 'strike':
          case 'del':
          case 'span':
            // Inline elements at top level, wrap in paragraph
            return (
              <View key={key} style={styles.paragraph}>
                <Text style={styles.text}>
                  {renderInlineContent([node], key)}
                </Text>
              </View>
            );
          case 'br':
            return <View key={key} style={{ height: 4 }} />;
          default:
            // Recursively render children for unknown block elements
            return <View key={key}>{renderNodes(children, key)}</View>;
        }
      }

      return null;
    });
  };

  return <View style={styles.container}>{renderNodes(parsed)}</View>;
};

export default HtmlPdf;
